/**
 * P-22 兑换请求记录（docs/03-UIUX.md §5）。
 *
 * 只读流水表：每一次客户端兑换尝试（含幂等回放）都在此留痕。
 * 核心用途是排查「同一授权码被尝试兑换多次」——按 `request_digest` 分组，
 * 同 digest 的后续行标记为「幂等命中」。行展开显示完整响应体。
 *
 * 与审计日志一致：倒序、分页、五态齐全。服务端不存授权码明文，因此「授权码」列
 * 只能展示请求体 SHA-256 指纹（`request_digest`），不能回显真实码值。
 */
import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBlock, EmptyFirstRun, TableSkeleton } from '../components/DataState';
import { Pager, DEFAULT_PAGE_SIZE, type PageSize } from '../components/Pager';
import { CopyButton } from '../components/CopyButton';
import { describeError } from '../api/client';
import { listRedemptionRequests, type RedemptionRequest } from '../api/endpoints';
import { useAsync } from '../lib/useAsync';
import { formatDateTime, middleTruncate } from '../lib/format';
import { Icon } from '../icons/Icon';
import { cssVar, ICON_SIZE, type TokenName } from '../tokens';

interface ResultDescriptor {
  token: TokenName;
  icon: 'circle-check' | 'repeat' | 'octagon-x';
  label: string;
}

function resultOf(code: number): ResultDescriptor {
  if (code === 200) return { token: 'st-done', icon: 'circle-check', label: '兑换成功' };
  if (code === 409) return { token: 'st-hold', icon: 'repeat', label: '已兑换·回放' };
  return { token: 'st-danger', icon: 'octagon-x', label: `失败 ${code}` };
}

export function RedemptionsPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const requests = useAsync(
    useCallback(() => listRedemptionRequests({ page, limit }), [page, limit]),
    [page, limit],
  );

  const list: readonly RedemptionRequest[] =
    requests.state.kind === 'ready' ? requests.state.data.items : [];
  const total = requests.state.kind === 'ready' ? requests.state.data.total : 0;
  const hasMore = requests.state.kind === 'ready' ? requests.state.data.hasMore : false;

  // 幂等命中：按 digest 分组，升序排列里首次出现之后、同 digest 的行标记为「幂等命中」。
  const replayIds = useMemo(() => {
    const seen = new Set<string>();
    const replay = new Set<string>();
    [...list]
      .sort((a, b) => a.created_at - b.created_at)
      .forEach((r) => {
        if (seen.has(r.request_digest)) replay.add(r.id);
        else seen.add(r.request_digest);
      });
    return replay;
  }, [list]);

  const toggle = (id: string) => setExpandedId((cur) => (cur === id ? null : id));

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">兑换请求记录</h1>
          <p className="page-head__sub">
            只读留痕每一次兑换尝试（含幂等回放），用于排查同一授权码被重复尝试兑换。点击行首箭头展开完整响应。
          </p>
        </div>
      </div>

      {requests.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(requests.state.error, '加载兑换请求')} onRetry={requests.reload} />
        </div>
      ) : requests.state.kind === 'loading' ? (
        <div className="table-frame">
          <table className="table">
            <TableSkeleton rows={12} columns={7} />
          </table>
        </div>
      ) : list.length === 0 ? (
        <div className="table-frame">
          <EmptyFirstRun
            icon="repeat"
            description="暂无兑换请求记录。客户端每次兑换尝试（含幂等回放）都会在此留痕，用于排查同一授权码被重复尝试。"
          />
        </div>
      ) : (
        <>
          <div className="table-frame">
            <table className="table">
              <thead>
                <tr>
                  <th aria-label="展开详情" />
                  <th>时间</th>
                  <th>授权码指纹</th>
                  <th>关联授权</th>
                  <th>客户端指纹</th>
                  <th>状态</th>
                  <th>幂等标记</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => {
                  const expanded = expandedId === r.id;
                  const isReplay = replayIds.has(r.id);
                  const result = resultOf(r.status_code);
                  return (
                    <FragmentRow
                      key={r.id}
                      request={r}
                      expanded={expanded}
                      isReplay={isReplay}
                      result={result}
                      onToggle={() => toggle(r.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager
            page={page}
            limit={limit}
            total={total}
            hasMore={hasMore}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        </>
      )}
    </>
  );
}

function FragmentRow({
  request,
  expanded,
  isReplay,
  result,
  onToggle,
}: {
  request: RedemptionRequest;
  expanded: boolean;
  isReplay: boolean;
  result: ResultDescriptor;
  onToggle: () => void;
}) {
  return (
    <>
      <tr>
        <td className="redeem-row-toggle">
          <button
            type="button"
            className="btn btn--quiet btn--icon"
            aria-expanded={expanded}
            aria-label={expanded ? '收起详情' : '展开详情'}
            onClick={onToggle}
          >
            <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={ICON_SIZE.inline} />
          </button>
        </td>
        <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>
          {formatDateTime(request.created_at)}
        </td>
        <td>
          <code className="redeem-digest" title={request.request_digest}>
            {middleTruncate(request.request_digest, 10, 6)}
          </code>
        </td>
        <td>
          {request.license_id === null ? (
            <span className="text-muted">—</span>
          ) : (
            <Link to={`/licenses/${request.license_id}`} className="link-mono">
              {middleTruncate(request.license_id, 8, 4)}
            </Link>
          )}
        </td>
        <td>
          <span className="redeem-idem">
            <code className="redeem-digest" title={request.idempotency_key}>
              {middleTruncate(request.idempotency_key, 10, 6)}
            </code>
            <span
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <CopyButton value={request.idempotency_key} what="幂等键" />
            </span>
          </span>
        </td>
        <td>
          <span className="redeem-result" style={{ '--st': cssVar(result.token) } as CSSProperties} title={`HTTP ${request.status_code}`}>
            <Icon name={result.icon} size={ICON_SIZE.badge} />
            {result.label}
          </span>
        </td>
        <td>
          {isReplay ? (
            <span className="redeem-flag" style={{ '--st': cssVar('st-hold') } as CSSProperties}>
              <Icon name="repeat" size={ICON_SIZE.badge} />
              幂等命中
            </span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
      </tr>
      {expanded ? (
        <tr className="redeem-detail">
          <td colSpan={7}>
            <div className="redeem-detail__grid">
              <span className="redeem-detail__key">请求 ID</span>
              <span className="redeem-detail__val">{request.id}</span>
              <span className="redeem-detail__key">授权码指纹</span>
              <span className="redeem-detail__val">{request.request_digest}</span>
              <span className="redeem-detail__key">客户端指纹（幂等键）</span>
              <span className="redeem-detail__val">{request.idempotency_key}</span>
              <span className="redeem-detail__key">关联授权</span>
              <span className="redeem-detail__val">{request.license_id ?? '—'}</span>
              <span className="redeem-detail__key">状态</span>
              <span className="redeem-detail__val">HTTP {request.status_code}</span>
            </div>
            <div className="redeem-response">{request.response_body}</div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
